/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus } from 'lucide-react';
import { fileToBase64 } from '@/lib/utils-cat';
import { toast } from 'sonner';
import { TYPE_META, BOARD_TYPES, EMPTY_BOARD_FORM } from './typeMeta';

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const TypeSelector = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="bacheca-type-selector">
      {BOARD_TYPES.map((k) => {
        const meta = TYPE_META[k]; const Icon = meta.icon;
        const active = value === k;
        return (
          <button key={k} type="button" onClick={() => onChange(k)}
            className={`p-2 rounded-xl border text-xs flex items-center gap-1.5 justify-center ${active ? 'ring-2 ring-primary/50 border-primary/50 bg-primary/5' : 'border-border hover:bg-secondary/50'}`}
            data-testid={`bacheca-type-${k}`}>
            <Icon className="h-4 w-4" style={{ color: meta.color }} />
            {t(k)}
          </button>
        );
      })}
    </div>
  );
};

const CategoryChips = ({ value, onChange }) => {
  const { t } = useTranslation();
  const options = [['', t('audienceAll')], ['medici', t('medici')], ['infermieri', t('infermieri')], ['oss', t('oss')]];
  return (
    <div className="mt-2 flex gap-1 flex-wrap">
      {options.map(([k, label]) => (
        <button key={k || 'all'} type="button" onClick={() => onChange(k)}
          className={`text-[11px] px-2 py-1 rounded-full border ${value === k ? 'bg-primary/10 border-primary/30 text-primary' : 'border-border text-muted-foreground'}`}
          data-testid={`bacheca-category-${k || 'all'}`}>
          {label}
        </button>
      ))}
    </div>
  );
};

const DateFields = ({ form, set }) => {
  const { t } = useTranslation();
  if (form.type === 'initiative' || form.type === 'project') {
    return (
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('startDate')}><Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} data-testid="bacheca-startdate-input" /></Field>
        <Field label={t('endDate')}><Input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} data-testid="bacheca-enddate-input" /></Field>
      </div>
    );
  }
  if (form.type === 'help' || form.type === 'job') {
    return <Field label={t('deadline')}><Input type="date" value={form.deadline} onChange={(e) => set('deadline', e.target.value)} data-testid="bacheca-deadline-input" /></Field>;
  }
  return null;
};

const ImagePicker = ({ value, onPick }) => {
  const { t } = useTranslation();
  const handleImg = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    onPick(await fileToBase64(f));
    e.target.value = '';
  };
  return (
    <Field label={t('images')}>
      <div className="mt-1">
        <label className="text-xs px-3 py-1.5 rounded-full bg-secondary cursor-pointer inline-block">{t('addImages')}
          <input type="file" accept="image/*" className="hidden" onChange={handleImg} data-testid="bacheca-image-input" />
        </label>
        {value && (
          <div className="mt-2 aspect-video rounded-xl overflow-hidden bg-secondary">
            <img src={value} alt="initiative" className="w-full h-full object-cover" />
          </div>
        )}
      </div>
    </Field>
  );
};

export const CreateBoardItemDialog = ({ onCreated, defaultType = 'initiative' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_BOARD_FORM, type: defaultType });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const canSubmit = form.title.trim() && form.description.trim();

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await api.post('/initiatives', { ...form, category: form.category || null });
      onCreated && onCreated(res.data);
      toast.success(t('initiativeCreated'));
      setOpen(false);
      setForm({ ...EMPTY_BOARD_FORM, type: defaultType });
    } catch (err) {
      // 409 = stesso annuncio già pubblicato nelle ultime 24h (anti-duplicati server)
      toast.error(err?.response?.status === 409 ? (err.response.data?.detail || t('errorOccurred')) : t('errorOccurred'));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="bacheca-create-open-button">
          <Plus className="h-4 w-4" />{t('createInitiative')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[560px] max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t('createInitiative')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label={t('type')}><TypeSelector value={form.type} onChange={(v) => set('type', v)} /></Field>
          <Field label={t('title')}><Input value={form.title} onChange={(e) => set('title', e.target.value)} data-testid="bacheca-title-input" /></Field>
          <Field label={t('description')}><Textarea rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} data-testid="bacheca-description-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('location')}><Input value={form.location} onChange={(e) => set('location', e.target.value)} data-testid="bacheca-location-input" /></Field>
            <Field label={t('category')}><CategoryChips value={form.category} onChange={(v) => set('category', v)} /></Field>
          </div>
          <DateFields form={form} set={set} />
          <Field label={t('contactInfo')}><Input value={form.contact_info} onChange={(e) => set('contact_info', e.target.value)} placeholder="email / tel / link" data-testid="bacheca-contact-input" /></Field>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch id="paid" checked={form.is_paid} onCheckedChange={(v) => set('is_paid', v)} data-testid="bacheca-ispaid-switch" />
              <Label htmlFor="paid" className="text-sm">{t('isPaid')}</Label>
            </div>
            {form.is_paid && (<Input placeholder={t('price')} value={form.price} onChange={(e) => set('price', e.target.value)} className="max-w-[160px]" data-testid="bacheca-price-input" />)}
          </div>
          <ImagePicker value={form.image_base64} onPick={(b64) => set('image_base64', b64)} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="bacheca-create-cancel-button">{t('cancel')}</Button>
          <Button onClick={submit} disabled={submitting || !canSubmit} data-testid="bacheca-create-submit-button">
            {submitting ? t('loading') : t('publish')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
